import { Link, Outlet, useParams, useRouterState } from '@tanstack/react-router';
import { DollarSign, MapPin, MessagesSquare } from 'lucide-react';
import { formatAddressInline } from '@buildbee/domain';
import { EmptyState, ErrorState, LoadingState, formatMoney, segmentedItemClass, segmentedListClass } from '@buildbee/ui';
import { errorCode, errorMessage, trpc } from '../../../lib/trpc';
import { usesCommercialDocs, withFeatures, type Feature } from '../../../lib/features';
import { StatusBadge } from '../components';
import { ProjectContext, type ProjectDetail } from '../lib';

type TabPath =
  | '/projects/$projectId'
  | '/projects/$projectId/schedule'
  | '/projects/$projectId/team'
  | '/projects/$projectId/documents'
  | '/projects/$projectId/drawings'
  | '/projects/$projectId/rfis'
  | '/projects/$projectId/submittals'
  | '/projects/$projectId/daily-logs'
  | '/projects/$projectId/photos'
  | '/projects/$projectId/punch'
  | '/projects/$projectId/time'
  | '/projects/$projectId/meetings'
  | '/projects/$projectId/bids'
  | '/projects/$projectId/chat';
type Tab = {
  to: TabPath;
  label: string;
  visible: (p: ProjectDetail) => boolean;
  exact?: boolean;
  /** Release flag (lib/features.ts): hidden while off. */
  feature?: Feature;
  /** Commercial document workflow: shown for commercial GCs and specialty subs only. */
  commercial?: boolean;
};

const TABS: Tab[] = [
  { to: '/projects/$projectId', label: 'Overview', visible: () => true, exact: true },
  { to: '/projects/$projectId/schedule', label: 'Schedule', visible: (p) => p.access.modules.schedule.view },
  { to: '/projects/$projectId/team', label: 'Team', visible: () => true },
  { to: '/projects/$projectId/chat', label: 'Chat', visible: () => true },
  {
    to: '/projects/$projectId/documents',
    label: 'Documents',
    visible: (p) => p.access.modules.documents.view,
  },
  { to: '/projects/$projectId/drawings', label: 'Drawings', visible: (p) => p.access.modules.drawings.view, commercial: true },
  { to: '/projects/$projectId/rfis', label: 'RFIs', visible: (p) => p.access.modules.rfis.view, commercial: true },
  {
    to: '/projects/$projectId/submittals',
    label: 'Submittals',
    visible: (p) => p.access.modules.submittals.view,
    commercial: true,
  },
  {
    to: '/projects/$projectId/daily-logs',
    label: 'Daily logs',
    visible: (p) => p.access.modules.dailyLogs.view,
  },
  { to: '/projects/$projectId/photos', label: 'Photos', visible: (p) => p.access.modules.photos.view },
  { to: '/projects/$projectId/punch', label: 'Punch list', visible: (p) => p.access.modules.punch.view },
  // Time: managers review the crew's hours; budget viewers see labour cost.
  { to: '/projects/$projectId/time', label: 'Time', visible: (p) => !!(p.access.modules.dailyLogs.approve || p.access.canViewBudget) },
  { to: '/projects/$projectId/meetings', label: 'Meetings', visible: (p) => p.access.modules.meetings.view, feature: 'project.meetings' },
  { to: '/projects/$projectId/bids', label: 'Bids', visible: (p) => p.access.modules.bids.view, feature: 'project.bids' },
];

/**
 * Tabs for this project and workspace: release flags first, then the business type (RFIs,
 * submittals and drawings are for commercial work), then the user's access.
 */
export function projectTabs(p: ProjectDetail, businessType: string | null | undefined): Tab[] {
  const commercial = usesCommercialDocs(businessType);
  return withFeatures(TABS).filter((t) => (!t.commercial || commercial) && t.visible(p));
}

export function ProjectLayout() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const project = trpc.projects.get.useQuery({ id: projectId });
  const workspace = trpc.workspace.current.useQuery(undefined, { staleTime: 5 * 60_000 });

  if (project.isLoading) return <LoadingState label="Loading project" />;
  if (project.error || !project.data) {
    if (errorCode(project.error) === 'NOT_FOUND') {
      return (
        <EmptyState
          title="Project not found"
          description="It may have been archived, or you are not on this project's team."
          action={
            <Link to="/projects" className="text-sm font-medium underline">
              Back to projects
            </Link>
          }
        />
      );
    }
    return <ErrorState error={errorMessage(project.error)} onRetry={() => void project.refetch()} />;
  }
  const p = project.data;
  const address = formatAddressInline(p.address);

  return (
    <ProjectContext.Provider value={p}>
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="text-[13px] text-muted-foreground">
            <Link to="/projects" className="hover:underline">
              Projects
            </Link>{' '}
            / <span className="figures">{p.number}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.03em] sm:text-[36px]">{p.name}</h1>
            <StatusBadge status={p.status} />
            {p.archivedAt ? <StatusBadge status="archived" /> : null}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {p.client ? (
              <span>
                Client:{' '}
                {p.access.canViewContacts ? (
                  <Link
                    to="/crm/contacts/$contactId"
                    params={{ contactId: p.client.id }}
                    className="hover:underline"
                  >
                    {p.client.name}
                  </Link>
                ) : (
                  p.client.name
                )}
              </span>
            ) : null}
            {p.pm ? <span>PM: {p.pm.name}</span> : null}
            {address ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-3.5" aria-hidden /> {address}
              </span>
            ) : null}
            {p.access.canViewBudget ? (
              <Link
                to="/financials/projects/$projectId"
                params={{ projectId: p.id }}
                className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
              >
                <DollarSign className="size-3.5" aria-hidden /> Budget &amp; billing
              </Link>
            ) : null}
            {p.access.canManagePortal ? (
              <Link
                to="/portal/admin/projects/$projectId"
                params={{ projectId: p.id }}
                className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
              >
                <MessagesSquare className="size-3.5" aria-hidden /> Client portal
              </Link>
            ) : null}
          </div>
        </div>
        <dl className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-px overflow-hidden rounded-xl bg-border shadow-card">
          {p.access.canViewBudget ? (
            <>
              <KeyFigure label="Contract" value={formatMoney(p.contractValueCents)} />
              <KeyFigure label="Budget" value={formatMoney(p.budgetCents)} />
            </>
          ) : null}
          <KeyFigure label="Complete" value={`${p.percentComplete}%`} />
        </dl>
        <nav aria-label="Project sections" className={segmentedListClass}>
          {projectTabs(p, workspace.data?.workspace.businessType).map((t) => {
            const href = t.to.replace('$projectId', p.id);
            const active = t.exact ? pathname === href || pathname === `${href}/` : pathname.startsWith(href);
            return (
              <Link
                key={t.label}
                to={t.to}
                params={{ projectId: p.id }}
                className={segmentedItemClass(active)}
              >
                {t.label}
              </Link>
            );
          })}
        </nav>
        <Outlet />
      </div>
    </ProjectContext.Provider>
  );
}

function KeyFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 bg-card px-5 py-4">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="figures text-[22px] font-medium leading-tight">{value}</dd>
    </div>
  );
}
